import { Component, HostListener, OnInit, computed, effect, input, output, signal } from '@angular/core';
import * as _ from 'lodash';
import { TooltipPosition, TooltipPositionX, TooltipPositionY } from '../fcp-tooltip';

@Component({
    selector: 'fcp-dropdown-tree',
    templateUrl: './fcp-dropdown-tree.component.html',
    styleUrls: ['./fcp-dropdown-tree.component.less'],
    standalone: false
})
export class FcpDropdownTreeComponent implements OnInit {
    // Signal inputs
    title = input('Placeholder');
    titleIcon = input({ show: false, value: '' });
    searchTitle = input('Placeholder');
    menu = input<DropdownTreeMenu>();
    allowSearch = input(false);
    notFoundMessage = input('No results found');
    selectedItemValue = input<string | string[]>();
    disabled = input(false);
    dataE2eHook = input<string>();
    isMulti = input(false);
    hasMultiSelectToggle = input(false);
    toggleLabel = input('Multi Select');

    // Signal outputs
    itemSelected = output<string | string[]>();
    isMultiChange = output<boolean>();

    // Internal state signals
    private _isOpen = signal(false);
    private _selected = signal<DropdownTreeItem | DropdownTreeItem[]>(undefined);
    private _searchQuery = signal('');
    private _isMultiSelectEnabled = signal(false);

    // Public computed signals for template access
    isOpen = computed(() => this._isOpen());
    selected = computed(() => this._selected());
    searchQuery = computed(() => this._searchQuery());
    isMultiSelectEnabled = computed(() => this._isMultiSelectEnabled());

    filteredSections = computed(() => {
        const menu = this.menu();
        const query = this._searchQuery();
        if (!menu) {
            return [];
        }
        if (query.length === 0) {
            return menu.sections;
        }
        const filterItems = (items, q) => items.filter((item) => item.label.toLowerCase().includes(q.toLowerCase()));
        const filterSections = (section) => ({
            ...section,
            items: filterItems(section.items, query)
        });
        return menu.sections.map(filterSections).filter((section) => section.items.length > 0);
    });

    // Static UI properties (keeping as regular properties)
    public tooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Bottom
    };

    public showBadgeTooltip = false;
    private clickedInside: boolean;

    constructor() {
        // Effect to sync multi-select state from input
        effect(() => {
            this._isMultiSelectEnabled.set(this.isMulti());
        });

        // Effect to update selection when inputs change
        effect(() => {
            const menu = this.menu();
            const itemValue = this.selectedItemValue();
            if (menu && itemValue !== undefined) {
                this.updateSelection(menu, itemValue);
            }
        });
    }

    ngOnInit() {
        if (this.isMulti()) {
            this._isMultiSelectEnabled.set(true);
            if (!Array.isArray(this._selected())) {
                this._selected.set([]);
            }
        }
    }

    clearLocalSelect() {
        this._selected.set([]);
    }

    handleModeToggle() {
        const isMultiSelectMode = !this._isMultiSelectEnabled();
        this._isMultiSelectEnabled.set(isMultiSelectMode);
        this.isMultiChange.emit(isMultiSelectMode);

        const currentSelected = this._selected();
        if (isMultiSelectMode) {
            if (currentSelected && !Array.isArray(currentSelected)) {
                this._selected.set([currentSelected]);
            }
        } else {
            if (currentSelected && Array.isArray(currentSelected)) {
                this.handleDropdownItemSelect(currentSelected[0]);
            }
        }
    }

    handleContainerClick() {
        if (!this.disabled()) {
            this._isOpen.update((isOpen) => !isOpen);
        }
        const selected = this._selected();
        if (this._isMultiSelectEnabled() && !this.disabled()) {
            this.itemSelected.emit((selected as DropdownTreeItem[]).map((item) => item.value));
        }
        this.handleSearchQueryChange('');
    }

    handleSectionClick(section: DropdownTreeSection) {
        if (this.menu()?.isCollapsable) {
            section.isOpen = !section.isOpen;
        }
    }

    handleDropdownItemSelect(selectedItem: DropdownTreeItem) {
        if (this._isMultiSelectEnabled()) {
            const currentSelection = (this._selected() as DropdownTreeItem[]) || [];
            if (this.hasMultiSelectToggle() && _.isEqual(currentSelection, [selectedItem])) {
                return;
            }

            if (currentSelection.some((item) => item.value === selectedItem.value)) {
                this._selected.set(currentSelection.filter((item) => item.value !== selectedItem.value));
            } else {
                this._selected.set([...currentSelection, selectedItem]);
            }
        } else {
            this._selected.set(selectedItem);
            this.handleContainerClick();
            this.itemSelected.emit(selectedItem.value);
        }
    }

    isDropdownItemSelected(item: DropdownTreeItem) {
        const selected = this._selected();
        if (this._isMultiSelectEnabled()) {
            return ((selected as DropdownTreeItem[]) || []).some((selectedItem) => selectedItem.value === item.value);
        }
        return (selected as DropdownTreeItem)?.value === item.value;
    }

    handleSearchQueryChange(searchQuery: string) {
        this._searchQuery.set(searchQuery);
    }

    titleLabel = computed(() => {
        const selected = this._selected();
        const isMulti = this._isMultiSelectEnabled();
        const title = this.title();

        if (isMulti && Array.isArray(selected) && selected.length > 0) {
            return {
                text: `${title} (${selected.length})`,
                highlighted: true
            };
        } else if (!isMulti && selected && !Array.isArray(selected)) {
            return {
                text: selected.label,
                highlighted: false
            };
        }

        return { text: title, highlighted: false };
    });

    badgeText = computed(() => {
        const selected = this._selected();
        return !this._isMultiSelectEnabled() && (selected as DropdownTreeItem)?.badgeText;
    });

    badgeInversed = computed(() => {
        const selected = this._selected();
        return !this._isMultiSelectEnabled() ? ((selected as DropdownTreeItem)?.badgeInversed ?? true) : true;
    });

    badgeIcon = computed(() => {
        const selected = this._selected();
        return !this._isMultiSelectEnabled() && (selected as DropdownTreeItem)?.badgeIcon;
    });

    badgeTooltip = computed(() => {
        const selected = this._selected();
        return !this._isMultiSelectEnabled() && (selected as DropdownTreeItem)?.badgeTooltip;
    });

    @HostListener('click')
    clickInsideComponentListener() {
        this.clickedInside = true;
    }

    @HostListener('document:click')
    clickOutsideComponentListener() {
        if (!this.clickedInside && this._isOpen()) {
            this.handleContainerClick();
        }
        this.clickedInside = false;
    }

    private updateSelection(menu: DropdownTreeMenu, itemValue: string | string[]) {
        if (_.isNil(menu) || _.isNil(itemValue)) {
            if (this._isMultiSelectEnabled()) {
                this._selected.set([]);
            } else {
                this._selected.set(undefined);
            }
        } else {
            const items = _.flatMap(menu.sections, (section) => section.items);
            if (this._isMultiSelectEnabled()) {
                this._selected.set(items.filter((item) => (itemValue as string[]).includes(item?.value)));
            } else {
                const valueToFind = Array.isArray(itemValue) ? itemValue[0] : itemValue;
                this._selected.set(items.find((item) => item.value === valueToFind));
            }
        }
    }
}

export interface DropdownTreeItem<T = any> {
    label: string;
    value: T;
    badgeText?: string;
    badgeInversed?: boolean;
    badgeIcon?: string;
    badgeTooltip?: string;
}

export interface DropdownTreeSection {
    label: string;
    isOpen?: boolean;
    items: DropdownTreeItem[];
}

export interface DropdownTreeMenu {
    sections: DropdownTreeSection[];
    isCollapsable: boolean;
}

export interface TransformOptions<T> {
    itemLabel: (item: T) => string;
    itemValue: (item: T) => any;
    itemBadgeText?: (item: T) => string;
    itemBadgeInversed?: (item: T) => boolean;
    itemBadgeIcon?: (item: T) => string;
    itemBadgeTooltip?: (itme: T) => string;
    sectionGroupBy: (item: T) => string;
    sectionOrderBy?: (groupBy: string) => number;
    sectionLabel: (groupBy: string, items: DropdownTreeItem[]) => string;
    sectionIsOpen?: (groupBy: string) => boolean;
    isCollapsable: boolean;
}

export function transformArrayToDropdownTreeMenu<T>(items: T[], options: TransformOptions<T>): DropdownTreeMenu {
    const groupedItems = _.groupBy(items, options.sectionGroupBy);
    let groupByKeys = _.keys(groupedItems);

    if (options.sectionOrderBy) {
        groupByKeys = groupByKeys.sort(
            (groupByA, groupByB) => options.sectionOrderBy(groupByA) - options.sectionOrderBy(groupByB)
        );
    }

    const sections = groupByKeys.reduce((acc, groupBy) => {
        const sectionItems = groupedItems[groupBy].map((item) => ({
            label: options.itemLabel(item),
            value: options.itemValue(item),
            badgeText: options.itemBadgeText?.(item),
            badgeInversed: options.itemBadgeInversed?.(item) ?? true,
            badgeIcon: options.itemBadgeIcon?.(item),
            badgeTooltip: options.itemBadgeTooltip?.(item)
        }));

        if (sectionItems.length !== 0) {
            const newSection = {
                label: options.sectionLabel(groupBy, sectionItems),
                isOpen: !options.isCollapsable || options.sectionIsOpen?.(groupBy),
                items: sectionItems
            };
            acc.push(newSection);
        }

        return acc;
    }, []);

    return {
        sections,
        isCollapsable: options.isCollapsable
    };
}
